import { useLocalDeletionBlocker } from './useLocalDeletionBlocker';
import { useState } from 'react';
import { generationErrorCopy } from './generationCopy';
import { GenerationReadError } from './GenerationReadError';
import { GenerationStartDialog } from './GenerationStartDialog';
import { isGenerationBlocking } from './generationTypes';
import { useGenerationBlockers } from './useGenerationQueries';
import { useStartGeneration } from './useStartGeneration';

export function GenerationStartControl({ select }: { select: (id: string) => void }) {
  const blockers = useGenerationBlockers();
  const deletionBlocked = useLocalDeletionBlocker();
  const start = useStartGeneration(select);
  const [open, setOpen] = useState(false);
  const knownBlockers = blockers.data ?? [];
  const hasRecovery = knownBlockers.some((job) => job.status === 'recovery_required');
  const isBlocked = knownBlockers.some(isGenerationBlocking);
  const cannotStart = deletionBlocked || start.mutation.isPending || start.storageFailed || start.hasIntent || !blockers.isSuccess || blockers.isFetching || isBlocked;
  const confirm = async (): Promise<void> => {
    if (deletionBlocked || (!start.hasIntent && cannotStart)) return;
    await start.confirm();
    setOpen(false);
  };

  return (
    <section className="map-generations__panel" aria-label="Запуск генерации">
      <h2>Новый запуск</h2>
      <div className="map-generations__controls">
        <button className="admin-submit" type="button" disabled={cannotStart} onClick={() => setOpen(true)}>
          Запустить генерацию
        </button>
        {start.hasIntent && (
          <button className="workspace-secondary-button" type="button" disabled={deletionBlocked || start.mutation.isPending || start.storageFailed} onClick={() => setOpen(true)}>
            Повторить запрос запуска
          </button>
        )}
      </div>
      {deletionBlocked && <p role="status">Сохранённое удаление выполняется или его результат ещё не подтверждён. Новый запуск недоступен до проверки.</p>}
      {blockers.isPending && <p role="status">Проверка активных запусков…</p>}
      {blockers.isError && (
        <GenerationReadError error={blockers.error} retry={() => { void blockers.refetch(); }} pending={blockers.isFetching} label="Повторить проверку запусков" />
      )}
      {hasRecovery ? (
        <p role="alert">Публикация требует проверки оператором. Новый запуск недоступен до ручного восстановления.</p>
      ) : isBlocked && (
        <p role="status">Генерация уже выполняется. Новый запуск недоступен.</p>
      )}
      {start.hasIntent && !start.mutation.isPending && (
        <p role="status">Результат запуска пока неизвестен. Повторите запрос запуска: сохранённый ключ исключает повторное создание. Автоматического запуска нет.</p>
      )}
      {start.mutation.isError && <p role="alert">{generationErrorCopy(start.mutation.error)}</p>}
      {start.storageFailed && (
        <p role="alert">Не удалось сохранить подтверждение запуска в этой вкладке. Запуск не отправлен. Проверьте доступность хранилища браузера; если предыдущий запуск был отправлен, обратитесь к оператору.</p>
      )}
      {open && (
        <GenerationStartDialog pending={start.mutation.isPending} replay={start.hasIntent} close={() => setOpen(false)} confirm={() => { void confirm(); }} />
      )}
    </section>
  );
}
